import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import { pascalCase } from 'pascal-case';

const getSizeInPixel = (size, maxSize, opts) => {
  const options = {
    units: ['%', 'em', 'rem'],
    ...opts
  };
  let pixelSize;

  if (isNumber(size)) {
    pixelSize = size;
  } else if (isString(size)) {
    const sizeMatches = size.match(/^([-0-9.]+)(%|em|rem|px)$/i);

    if (sizeMatches) {
      const unit = sizeMatches[2].toLowerCase();
      const floatValue = parseFloat(sizeMatches[1]);
      const value = unit === 'rem' || unit === 'em' ? floatValue * 100 : floatValue;
      pixelSize = options.units.indexOf(unit) !== -1 ? value / 100 * maxSize : value;
    } else {
      pixelSize = !Number.isNaN(parseFloat(size)) ? parseFloat(size) : null;
    }
  } else {
    pixelSize = maxSize;
  }

  return pixelSize;
};

const getSizeWithinBounds = (width, height, maxWidth, maxHeight, opts) => {
  const options = {
    cover: false,
    ...opts
  };

  if (!width || width <= 0 || !height || height <= 0) {
    return {
      width: 0,
      height: 0,
      ratio: 1
    };
  }

  const scaleWidth = Math.round(maxWidth || width) / width;
  const scaleHeight = Math.round(maxHeight || height) / height;
  const scale = options.cover ? Math.max(scaleWidth, scaleHeight) : Math.min(scaleWidth, scaleHeight);
  const computedWidth = Math.round(width * scale);
  const computedHeight = Math.round(height * scale);
  return {
    width: computedWidth,
    height: computedHeight,
    scale
  };
};

const getSizeFromString = (size, width, height, maxWidth, maxHeight, opts) => {
  const {
    force = false,
    autoIsNull = true,
    round = false,
    cover = false
  } = { ...opts
  };
  let returnSize = {
    width: maxWidth,
    height: maxHeight
  };
  const ratio = width / height;

  if (size === 'cover') {
    returnSize = getSizeWithinBounds(width, height, maxWidth, maxHeight, {
      cover: true
    });
  } else {
    const sizeArray = typeof size === 'string' ? size.split(' ') : size;
    const sizeWidth = sizeArray[0];
    const sizeHeight = sizeArray[1] || sizeWidth;
    const widthPixel = getSizeInPixel(sizeWidth, maxWidth);
    const heightPixel = getSizeInPixel(sizeHeight, maxHeight);

    if (widthPixel !== null && heightPixel !== null) {
      returnSize = force ? {
        width: widthPixel,
        height: heightPixel
      } : getSizeWithinBounds(width, height, widthPixel, heightPixel, {
        cover
      });
    } else if (widthPixel !== null) {
      returnSize.width = widthPixel;

      if (sizeHeight === 'auto') {
        const relativeHeight = ratio !== 0 ? returnSize.width / ratio : 0;
        returnSize.height = !autoIsNull ? relativeHeight : null;
      } else {
        returnSize.height = sizeHeight;
      }
    } else if (heightPixel !== null) {
      returnSize.height = heightPixel;

      if (sizeWidth === 'auto') {
        const relativeWidth = ratio !== 0 ? returnSize.height * ratio : 0;
        returnSize.width = !autoIsNull ? relativeWidth : null;
      } else {
        returnSize.width = sizeWidth;
      }
    } else {
      if (sizeWidth === 'auto') {
        returnSize.width = !autoIsNull ? width : null;
      } else {
        returnSize.width = sizeWidth;
      }

      if (sizeHeight === 'auto') {
        returnSize.height = !autoIsNull ? height : null;
      } else {
        returnSize.height = sizeHeight;
      }
    }
  }

  if (returnSize.width !== null && round) {
    returnSize.width = Math.round(returnSize.width);
  }

  if (returnSize.height !== null && round) {
    returnSize.height = Math.round(returnSize.height);
  }

  return returnSize;
};

const getPositionFromString = (positionString, width, height, maxWidth, maxHeight, opts) => {
  // eslint-disable-next-line no-unused-vars
  const {
    relativeToElement = false
  } = { ...opts
  };
  const positionArray = positionString.split(' ');
  const firstPositionParts = positionArray[0].split(':');
  const secondPositionParts = (positionArray[1] || positionArray[0]).split(':');
  const firstPosition = firstPositionParts[0];
  const secondPosition = secondPositionParts[0];
  const firstPositionValue = firstPositionParts[1] || null;
  const secondPositionValue = secondPositionParts[1] || null;
  const horizontalFirst = firstPosition === 'left' || firstPosition === 'right' || secondPosition === 'top' || secondPosition === 'bottom' || firstPosition === 'center' && secondPosition === 'center';
  const horizontal = horizontalFirst ? firstPosition : secondPosition;
  const vertical = horizontalFirst ? secondPosition : firstPosition;
  const horizontalOffset = getSizeInPixel(horizontalFirst ? firstPositionValue || 0 : secondPositionValue || 0, relativeToElement ? width : maxWidth);
  const verticalOffset = getSizeInPixel(horizontalFirst ? secondPositionValue || 0 : firstPositionValue || 0, relativeToElement ? height : maxHeight);
  const position = {
    x: 0,
    y: 0,
    horizontal,
    vertical
  };
  const style = {
    position: 'absolute',
    top: 'auto',
    bottom: 'auto',
    left: 'auto',
    right: 'auto'
  };

  if (horizontal === 'center') {
    position.x = (maxWidth - width) / 2;
    style.left = (maxWidth - width) / 2;
  } else if (horizontal === 'right') {
    position.x = maxWidth - width;
    style.right = 0;
  } else {
    style.left = 0;
  }

  if (horizontal === 'right') {
    position.x -= horizontalOffset;
    style.right += horizontalOffset;
  } else {
    position.x += horizontalOffset;
    style.left += horizontalOffset;
  }

  if (vertical === 'center') {
    position.y = (maxHeight - height) / 2;
    style.top = (maxHeight - height) / 2;
  } else if (vertical === 'bottom') {
    position.y = maxHeight - height;
    style.bottom = 0;
  } else {
    style.top = 0;
  }

  if (vertical === 'bottom') {
    position.y -= verticalOffset;
    style.bottom += verticalOffset;
  } else {
    position.y += verticalOffset;
    style.top += verticalOffset;
  }

  position.style = style;
  return position;
};

const paddingProps = ['paddingTop', 'paddingBottom', 'paddingLeft', 'paddingRight'];
const borderProps = ['borderLeftWidth', 'borderRightWidth', 'borderBottomWidth', 'borderTopWidth'];

const getNumberValue = value => {
  const matches = isString(value) ? value.match(/^([0-9.]+)/) : false;

  if (matches) {
    return parseFloat(matches[1]);
  }

  return isNumber(value) ? value : 0;
};

const getStyleValue = (style, prop) => getNumberValue(style[prop] || 0);

const parseUnits = (style, prop, direction) => {
  const value = style[prop] || 0;

  if (isNumber(value)) {
    return value;
  }

  const directionIndex = {
    top: 0,
    right: 1,
    bottom: 2,
    left: 3
  };
  const parts = value.split(' ');
  const partsCount = parts.length;

  if (partsCount === 1) {
    return getNumberValue(parts[0]);
  }

  const index = directionIndex[direction];
  const lastIndex = partsCount - 1;

  if (index > lastIndex) {
    return getNumberValue(parts[partsCount === 2 ? index % 2 : lastIndex]);
  }

  return getNumberValue(parts[index]);
};

const getStylesValue = (style, direction) => {
  const pascalDirection = pascalCase(direction); // Padding

  const padding = paddingProps.filter(prop => prop.match(new RegExp("".concat(pascalDirection))));
  const paddingsTotal = padding.reduce((total, prop) => total + getStyleValue(style, prop), 0);
  const paddingTotal = typeof style.padding !== 'undefined' ? parseUnits(style, 'padding', direction) : 0;
  const paddingSum = paddingTotal || paddingsTotal; // Borders

  const borders = borderProps.filter(prop => prop.match(new RegExp("".concat(pascalDirection))));
  const bordersTotal = borders.reduce((total, prop) => total + getStyleValue(style, prop), 0);
  const borderTotal = getStyleValue(style, 'border');
  const borderSum = borderTotal || bordersTotal;
  return paddingSum + borderSum;
};

const getElementInnerSize = (element, style) => {
  const elementStyle = style || window.getComputedStyle(element);
  const elementWidth = element.width || element.offsetWidth || 0;
  const elementHeight = element.height || element.offsetHeight || 0;
  const left = getStylesValue(elementStyle, 'left');
  const right = getStylesValue(elementStyle, 'right');
  const top = getStylesValue(elementStyle, 'top');
  const bottom = getStylesValue(elementStyle, 'bottom');
  const width = elementWidth - (left + right);
  const height = elementHeight - (top + bottom);
  return {
    width,
    height,
    left,
    right,
    top,
    bottom
  };
};

const getElementInnerWidth = (element, style) => getElementInnerSize(element, style).width;

const getElementInnerHeight = (element, style) => getElementInnerSize(element, style).height;

/* eslint-disable no-underscore-dangle, no-param-reassign */

/**
 * Detect Element Resize
 *
 * https://github.com/sdecima/javascript-detect-element-resize
 * Sebastian Decima
 *
 * version: 0.5.3
 */
const {
  attachEvent = null
} = typeof document !== 'undefined' ? document || {} : {};
let stylesCreated = false;

const requestFrame = (() => {
  const raf = (typeof window !== 'undefined' ? window.requestAnimationFrame || window.mozRequestAnimationFrame || window.webkitRequestAnimationFrame : null) || (fn => setTimeout(fn, 20));

  return fn => raf(fn);
})();

const cancelFrame = (() => {
  const cancel = (typeof window !== 'undefined' ? window.cancelAnimationFrame || window.mozCancelAnimationFrame || window.webkitCancelAnimationFrame : null) || clearTimeout;
  return id => cancel(id);
})();

const resetTriggers = element => {
  const triggers = element.__resizeTriggers__;
  const expand = triggers.firstElementChild;
  const contract = triggers.lastElementChild;
  const expandChild = expand.firstElementChild;
  contract.scrollLeft = contract.scrollWidth;
  contract.scrollTop = contract.scrollHeight;
  expandChild.style.width = "".concat(expand.offsetWidth + 1, "px");
  expandChild.style.height = "".concat(expand.offsetHeight + 1, "px");
  expand.scrollLeft = expand.scrollWidth;
  expand.scrollTop = expand.scrollHeight;
};

const scrollListener = e => {
  const element = e.currentTarget;
  resetTriggers(element);
  if (element.__resizeRAF__) cancelFrame(element.__resizeRAF__);
  element.__resizeRAF__ = requestFrame(() => {
    const {
      offsetWidth,
      offsetHeight
    } = element;

    if (offsetWidth !== element.__resizeLast__[0] || offsetHeight !== element.__resizeLast__[1]) {
      element.__resizeLast__[0] = offsetWidth;
      element.__resizeLast__[1] = offsetHeight;

      element.__resizeListeners__.forEach(fn => {
        fn.call(element, e);
      });
    }
  });
};
/* Detect CSS Animations support to detect element display/re-attach */


let animation = false;
let keyframeprefix = '';
let animationstartevent = 'animationstart';
const domPrefixes = 'Webkit Moz O ms'.split(' ');
const startEvents = 'webkitAnimationStart animationstart oAnimationStart MSAnimationStart'.split(' ');
let pfx = '';
const elm = typeof document !== 'undefined' ? document.createElement('fakeelement') : null;

if (elm !== null && typeof elm.style.animationName !== 'undefined') {
  animation = true;
}

if (animation === false) {
  for (let i = 0; i < domPrefixes.length; i += 1) {
    if (elm !== null && typeof elm.style["".concat(domPrefixes[i], "AnimationName")] !== 'undefined') {
      pfx = domPrefixes[i];
      keyframeprefix = "-".concat(pfx.toLowerCase(), "-");
      animationstartevent = startEvents[i];
      animation = true;
      break;
    }
  }
}

const animationName = 'resizeanim';
const animationKeyframes = "@".concat(keyframeprefix, "keyframes ").concat(animationName, " { from { opacity: 0; } to { opacity: 0; } } ");
const animationStyle = "".concat(keyframeprefix, "animation: 1ms ").concat(animationName, "; ");

const createStyles = () => {
  if (!stylesCreated) {
    // opacity:0 works around a chrome bug https://code.google.com/p/chromium/issues/detail?id=286360
    const css = "\n            ".concat(animationKeyframes || '', "\n            .resize-triggers { ").concat(animationStyle || '', "visibility: hidden; opacity: 0; }\n            .resize-triggers,\n            .resize-triggers > div,\n            .contract-trigger:before { content: \" \"; display: block; position: absolute; top: 0; left: 0; height: 100%; width: 100%; overflow: hidden; }\n            .resize-triggers > div { background: #eee; overflow: auto; }\n            .contract-trigger:before { width: 200%; height: 200%; }\n        ");
    const head = document.head || document.getElementsByTagName('head')[0];
    const style = document.createElement('style');
    style.type = 'text/css';

    if (style.styleSheet) {
      style.styleSheet.cssText = css;
    } else {
      style.appendChild(document.createTextNode(css));
    }

    head.appendChild(style);
    stylesCreated = true;
  }
};

const addResizeListener = (element, fn) => {
  if (attachEvent) {
    element.attachEvent('onresize', fn);
  } else {
    if (!element.__resizeTriggers__) {
      if (getComputedStyle(element).position === 'static') {
        element.style.position = 'relative';
      }

      createStyles();
      element.__resizeLast__ = [0, 0];
      element.__resizeListeners__ = [];
      element.__resizeTriggers__ = document.createElement('div');
      element.__resizeTriggers__.className = 'resize-triggers';
      element.__resizeTriggers__.innerHTML = '<div class="expand-trigger"><div></div></div><div class="contract-trigger"></div>';
      element.appendChild(element.__resizeTriggers__);
      resetTriggers(element);
      element.addEventListener('scroll', scrollListener, true);
      /* Listen for a css animation to detect element display/re-attach */

      element.__resizeTriggers__.addEventListener(animationstartevent, e => {
        if (e.animationName === animationName) {
          resetTriggers(element);
        }
      });
    }

    element.__resizeListeners__.push(fn);
  }
};

const removeResizeListener = (element, fn) => {
  if (attachEvent) {
    element.detachEvent('onresize', fn);
  } else {
    element.__resizeListeners__.splice(element.__resizeListeners__.indexOf(fn), 1);

    if (!element.__resizeListeners__.length) {
      element.removeEventListener('scroll', scrollListener);
      element.__resizeTriggers__ = !element.removeChild(element.__resizeTriggers__);
    }
  }
};

export { addResizeListener, getElementInnerHeight, getElementInnerSize, getElementInnerWidth, getPositionFromString, getSizeFromString, getSizeInPixel, getSizeWithinBounds, removeResizeListener };
